import React, { useState, useEffect, useCallback } from 'react';
import { Accessibility, X, ZoomIn, ZoomOut, Eye, Sun, Type, RotateCcw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DEFAULT_SETTINGS = {
  fontSize: 100,
  highContrast: false,
  darkMode: false,
  dyslexicFont: false,
  highlightLinks: false,
};

const translations = {
  en: {
    title: 'Accessibility',
    fontSize: 'Font Size',
    increase: 'Increase',
    decrease: 'Decrease',
    highContrast: 'High Contrast',
    darkMode: 'Dark Mode',
    dyslexicFont: 'Dyslexic Font',
    highlightLinks: 'Highlight Links',
    reset: 'Reset All',
    close: 'Close'
  },
  el: {
    title: 'Προσβασιμότητα',
    fontSize: 'Μέγεθος Γραμματοσειράς',
    increase: 'Αύξηση',
    decrease: 'Μείωση',
    highContrast: 'Υψηλή Αντίθεση',
    darkMode: 'Σκούρο Θέμα',
    dyslexicFont: 'Γραμματοσειρά Δυσλεξίας',
    highlightLinks: 'Επισήμανση Συνδέσμων',
    reset: 'Επαναφορά',
    close: 'Κλείσιμο'
  }
};

function applySettings(s) {
  const root = document.documentElement;
  root.style.fontSize = `${s.fontSize}%`;
  root.classList.toggle('high-contrast', s.highContrast);
  root.classList.toggle('dyslexic-font', s.dyslexicFont);
  root.classList.toggle('highlight-links', s.highlightLinks);
}

const ToggleButton = ({ active, icon: Icon, label, onToggle }) => (
  <button
    onClick={onToggle}
    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400 ${
      active
        ? 'bg-cyan-500/20 border border-cyan-500/50'
        : 'bg-white/5 hover:bg-white/10 border border-transparent'
    }`}
    aria-pressed={active}
  >
    <div className="flex items-center space-x-3">
      <Icon size={18} className={active ? 'text-cyan-400' : 'text-white/70'} />
      <span className="text-white text-sm">{label}</span>
    </div>
    <div className={`w-10 h-6 rounded-full transition-colors ${active ? 'bg-cyan-500' : 'bg-white/20'}`}>
      <div className={`w-5 h-5 mt-0.5 rounded-full bg-white transition-transform ${active ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
    </div>
  </button>
);

const FontSizeControls = ({ fontSize, onDecrease, onIncrease, t }) => (
  <div className="space-y-2">
    <label className="text-white/80 text-sm font-medium flex items-center space-x-2">
      <Type size={16} />
      <span>{t.fontSize}: {fontSize}%</span>
    </label>
    <div className="flex items-center space-x-2">
      <button
        onClick={onDecrease}
        disabled={fontSize <= 70}
        className="flex-1 flex items-center justify-center space-x-2 py-2 px-3 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label={t.decrease}
      >
        <ZoomOut size={18} />
        <span className="text-sm">A-</span>
      </button>
      <button
        onClick={onIncrease}
        disabled={fontSize >= 150}
        className="flex-1 flex items-center justify-center space-x-2 py-2 px-3 bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
        aria-label={t.increase}
      >
        <ZoomIn size={18} />
        <span className="text-sm">A+</span>
      </button>
    </div>
  </div>
);

const AccessibilityWidget = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const t = translations[language] || translations.en;

  // Load saved settings on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  // Persist and apply on change
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  // Escape key closes panel
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const increaseFontSize = () => setSettings(prev => ({ ...prev, fontSize: Math.min(prev.fontSize + 10, 150) }));
  const decreaseFontSize = () => setSettings(prev => ({ ...prev, fontSize: Math.max(prev.fontSize - 10, 70) }));
  const toggleSetting = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  const resetAll = () => setSettings(DEFAULT_SETTINGS);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 bottom-4 z-50 w-14 h-14 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-cyan-300"
        aria-label={t.title}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <Accessibility size={28} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            id="accessibility-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="accessibility-title"
            className="fixed left-4 bottom-24 z-50 w-80 bg-[#0a0e27] border border-cyan-500/30 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-cyan-500/20 to-blue-500/20">
              <div className="flex items-center space-x-3">
                <Accessibility className="text-cyan-400" size={24} />
                <h2 id="accessibility-title" className="text-white font-bold text-lg">{t.title}</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
                aria-label={t.close}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <FontSizeControls
                fontSize={settings.fontSize}
                onDecrease={decreaseFontSize}
                onIncrease={increaseFontSize}
                t={t}
              />
              <div className="space-y-2">
                <ToggleButton active={settings.highContrast} icon={Eye} label={t.highContrast} onToggle={() => toggleSetting('highContrast')} />
                <ToggleButton active={settings.dyslexicFont} icon={Type} label={t.dyslexicFont} onToggle={() => toggleSetting('dyslexicFont')} />
                <ToggleButton active={settings.highlightLinks} icon={Sun} label={t.highlightLinks} onToggle={() => toggleSetting('highlightLinks')} />
              </div>
              <button
                onClick={resetAll}
                className="w-full flex items-center justify-center space-x-2 py-3 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                <RotateCcw size={18} />
                <span className="text-sm">{t.reset}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
